// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { CHECKOUT_STEPS } from "@plumberssupply/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@plumberssupply/App/Widgets/CheckoutStepper/StepperContext";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@plumberssupply/Overrides/Components/AddressInfoDisplay";
import { EMPTY_VALUE } from "@plumberssupply/XDL/constants";
import IconEditSquare from "@plumberssupply/XDL/Icons/IconEditSquare";
import {
    color,
    formatDateTimeDisplay,
    iconLinkCommonStyles,
    iconStyles,
    mergeWithCss,
} from "@plumberssupply/XDL/Utilities";
import React, { useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { requestedDeliveryDate } = state.pages.checkoutReviewAndSubmit;

    return {
        cartState,
        billToState: getBillToState(state, cartState.value?.billToId),
        shipToState: getShipToState(state, cartState.value?.shipToId),
        orderState: getOrderState(state, cartState.value?.orderNumber),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        language: state.context.session.language,
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        requestedDeliveryDate,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
};

export interface CheckoutReviewOrderStyles {
    OrderInformationGridContainer?: GridContainerProps;
    shippingInformationGridItem?: GridItemProps;
    billingInformationGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    notesDescription?: TypographyPresentationProps;
    orderInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    orderNotesGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    addressesWrapperGridContainer?: GridContainerProps;
    orderAdditionalDetailsGridContainer?: GridContainerProps;
    shippingDateRequestedText?: TypographyPresentationProps;
    shippingDateRequestedTitle?: TypographyPresentationProps;
    shippingInformationDateRequestedGridItem?: GridItemProps;
    sectionHeadingGridItem?: GridItemProps;
    sectionHeadingStyles?: TypographyPresentationProps;
    addressHeadingText?: TypographyPresentationProps;
    editSectionButton?: LinkPresentationProps;
}

export const orderInformationStyles: CheckoutReviewOrderStyles = {
    OrderInformationGridContainer: {
        gap: 20,
        css: css`
            padding: 0 15px;
            width: 100%;
        `,
    },
    shippingInformationGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    billingInformationGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    notesGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            flex-direction: column;
        `,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            color: "common.ui11",
            weight: 800,
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
        text: {
            size: 14,
        },
    },
    orderInformationGridItems: {
        width: [12, 6, 6, 4, 4],
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    addressHeadingText: {
        variant: "h5",
        color: "common.ui11",
        css: css`
            margin-bottom: 16px;
        `,
    },
    orderNotesGridItem: {
        width: 8,
    },
    addressesWrapperGridContainer: {
        css: css`
            padding-top: 16px;
            padding-bottom: 32px;
            border-bottom: 1px solid ${color("common.ui7")};
        `,
    },
    shippingInformationDateRequestedGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    shippingDateRequestedTitle: {
        color: "common.ui11",
        variant: "h5",
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    shippingDateRequestedText: {
        size: 14,
        css: css`
            display: block;
        `,
    },
    sectionHeadingGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
            align-items: center;
        `,
    },
    sectionHeadingStyles: {
        color: "common.ui11",
        variant: "h3",
        css: css`
            margin-bottom: 5px;
            margin-right: 24px;
        `,
    },
    orderAdditionalDetailsGridContainer: {
        css: css`
            padding: 32px 0 42px 0;
        `,
    },
    editSectionButton: {
        ...mergeWithCss(iconLinkCommonStyles(IconEditSquare), { ...iconStyles }),
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = orderInformationStyles;

const CheckoutReviewOrder = ({
    cartState,
    billToState,
    shipToState,
    loadBillTo,
    loadShipTo,
    checkoutShippingPage,
    pickUpWarehouse,
    requestedDeliveryDate,
}: Props) => {
    const { currentStep, steps, setCurrentStep, checkoutInfoState } = useContext(StepperContext);
    const history = useHistory();

    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }

        const cart = cartState.value;
        if (!billToState.isLoading && !billToState.value && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }

        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [cartState]);

    const cart = cartState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    return (
        <GridContainer {...styles.OrderInformationGridContainer}>
            <GridContainer {...styles.addressesWrapperGridContainer}>
                <GridItem {...styles.sectionHeadingGridItem}>
                    <Typography {...styles.sectionHeadingStyles}>{translate("Addresses")}</Typography>
                    <Link
                        {...styles.editSectionButton}
                        type="button"
                        onClick={() => {
                            history.push(checkoutShippingPage?.url as string);
                        }}
                        data-test-selector="checkoutShipping_editOrderInformation"
                    >
                        {translate("Edit")}
                    </Link>
                </GridItem>

                <GridItem {...styles.billingInformationGridItem}>
                    <Typography {...styles.addressHeadingText} as="h3">
                        {translate("Billing Address")}
                    </Typography>
                    <AddressInfoDisplay
                        {...billTo}
                        state={billTo.state ? billTo.state.abbreviation : undefined}
                        country={billTo.country ? billTo.country.abbreviation : undefined}
                    />
                </GridItem>

                <GridItem {...styles.shippingInformationGridItem}>
                    {cart.fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse ? (
                        <>
                            <Typography {...styles.addressHeadingText} as="h3">
                                {translate("Pick Up Address")}
                            </Typography>
                            <AddressInfoDisplay
                                {...pickUpWarehouse}
                                companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                            />
                        </>
                    ) : (
                        <>
                            <Typography {...styles.addressHeadingText} as="h3">
                                {translate("Shipping Address")}
                            </Typography>
                            <AddressInfoDisplay
                                {...shipTo}
                                state={shipTo.state ? shipTo.state.abbreviation : undefined}
                                country={shipTo.country ? shipTo.country.abbreviation : undefined}
                            />
                        </>
                    )}
                </GridItem>

                {cart.fulfillmentMethod !== FulfillmentMethod.PickUp && requestedDeliveryDate && (
                    <GridItem {...styles.shippingInformationDateRequestedGridItem}>
                        <Typography {...styles.shippingDateRequestedTitle}>
                            {translate("Delivery Date & Time")}
                        </Typography>
                        <Typography
                            {...styles.shippingDateRequestedText}
                            data-test-selector="orderInformation_requestedDeliveryDate"
                        >
                            {formatDateTimeDisplay(requestedDeliveryDate.toISOString())}
                        </Typography>
                    </GridItem>
                )}

                {cart.fulfillmentMethod === FulfillmentMethod.PickUp && cart.requestedPickupDate && (
                    <GridItem {...styles.shippingInformationDateRequestedGridItem}>
                        <Typography {...styles.shippingDateRequestedTitle}>
                            {translate("Pick Up Date & Time")}
                        </Typography>
                        <Typography
                            {...styles.shippingDateRequestedText}
                            data-test-selector="orderInformation_requestedPickupDate"
                        >
                            {formatDateTimeDisplay(cart.requestedPickupDate)}
                        </Typography>
                    </GridItem>
                )}
            </GridContainer>

            <GridContainer {...styles.orderAdditionalDetailsGridContainer}>
                <GridItem {...styles.sectionHeadingGridItem}>
                    <Typography {...styles.sectionHeadingStyles}>{translate("Order Information")}</Typography>
                    <Link
                        {...styles.editSectionButton}
                        type="button"
                        onClick={() => {
                            setCurrentStep(CHECKOUT_STEPS.ORDER_INFO);
                        }}
                        data-test-selector="checkoutShipping_editOrderInformation"
                    >
                        {translate("Edit")}
                    </Link>
                </GridItem>

                {!cart.requiresApproval && (
                    <GridItem {...styles.orderInformationGridItems}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Purchase Order #")}
                            text={checkoutInfoState.poNumber || EMPTY_VALUE}
                        />
                    </GridItem>
                )}
                {!cart.requiresApproval && (
                    <GridItem {...styles.orderInformationGridItems}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Placed By")}
                            text={checkoutInfoState?.placedBy || EMPTY_VALUE}
                        />
                    </GridItem>
                )}
                {!cart.requiresApproval && (
                    <GridItem {...styles.orderInformationGridItems}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Phone Number")}
                            text={checkoutInfoState?.placedByPhoneNumber || EMPTY_VALUE}
                        />
                    </GridItem>
                )}
                {checkoutInfoState?.cartNotes && (
                    <GridItem {...styles.notesGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Order Notes")}
                            text={checkoutInfoState?.cartNotes}
                        />
                    </GridItem>
                )}
                {!cart.requiresApproval && (
                    <GridItem {...styles.orderInformationGridItems}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Payment Method")}
                            text={cart.paymentMethod?.description || EMPTY_VALUE}
                        />
                    </GridItem>
                )}
            </GridContainer>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewOrder),
    definition: {
        displayName: "Review Order",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        group: "Checkout - Review & Submit",
    },
};

export default widgetModule;
